import { Chip, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { AdsClick, Close } from '@mui/icons-material';
import Autocomplete from 'domains/core/components/Autocomplete';
import { greyChipStyles } from 'domains/core/components/Chip/Chip';
import { SegmentFilterEngagementFields, SegmentFilterState, SegmentFilterV2 } from 'domains/segments/types';
import useGetSendInteractions from './useGetSendInteractions';
import useSendClickableUrls from 'hooks/queries/useSendClickableUrls';

export type Props = {
    activeChannelEngagementFilter?: SegmentFilterV2;
    filter: SegmentFilterState<SegmentFilterEngagementFields>;
    index: number;
    sx?: {
        autocomplete: object;
    };
    updateSegmentFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterEngagementFields>,
        resetPreview?: boolean
    ) => void;
    disabled?: boolean;
    disabledTooltipContent?: React.ReactNode;
};

const ValueAutocomplete = ({
    activeChannelEngagementFilter,
    filter,
    index,
    sx,
    updateSegmentFilter,
    disabled,
    disabledTooltipContent,
}: Props) => {
    const supportsMultiSelect = !!activeChannelEngagementFilter;

    const { autocompleteValue: selectedSends, categorySpecificFields } = filter;
    const { sendInteractionField } = categorySpecificFields;

    let interactionAutocompleteValue: any, interactionAutocompleteInputValue: string, isValueAutocompleteVisible;
    if (sendInteractionField) {
        isValueAutocompleteVisible = sendInteractionField.value !== null;
        ({
            autocompleteValue: interactionAutocompleteValue,
            autocompleteInputValue: interactionAutocompleteInputValue,
        } = sendInteractionField);
    }

    const selectedSendValue =
        selectedSends && (Array.isArray(selectedSends) ? selectedSends[0]?.value : selectedSends?.value);
    const sendId = Number(selectedSendValue) || undefined;

    const { data: sendInteractionsData } = useGetSendInteractions({
        path: activeChannelEngagementFilter?.valuesRef,
        reactQueryOptions: { enabled: activeChannelEngagementFilter && !!sendId && isValueAutocompleteVisible },
        sendId,
    });

    const { data: sendClickableUrls } = useSendClickableUrls({
        sendId,
        reactQueryOptions: { enabled: !activeChannelEngagementFilter && !!sendId && isValueAutocompleteVisible },
    });

    const data = sendInteractionsData ?? sendClickableUrls;

    const sendInteractionOptions = data?.map((url) => ({
        label: url,
        value: url,
    }));

    const handleInputValueChange = (newInputValue: string) => {
        updateSegmentFilter(index, {
            ...filter,
            categorySpecificFields: {
                ...categorySpecificFields,
                sendInteractionField: {
                    ...sendInteractionField,
                    autocompleteInputValue: newInputValue,
                },
            },
        });
    };

    const handleValueChange = (value: any) => {
        updateSegmentFilter(index, {
            ...filter,
            categorySpecificFields: {
                ...categorySpecificFields,
                sendInteractionField: {
                    ...sendInteractionField,
                    value: value?.value,
                    autocompleteValue: value,
                },
            },
        });
    };

    const handleToggleButtonClick = () => {
        updateSegmentFilter(index, {
            ...filter,
            categorySpecificFields: {
                ...categorySpecificFields,
                sendInteractionField: {
                    autocompleteInputValue: '',
                    autocompleteValue: supportsMultiSelect ? [] : null,
                    value: '',
                },
            },
        });
    };

    const renderAutocomplete = () => {
        const sharedProps = {
            disabled,
            getOptionLabel: (option: any) => {
                if (typeof option === 'string') return option;
                return option?.label || option?.value || '';
            },
            inputValue: interactionAutocompleteInputValue ?? '',
            isOptionEqualToValue: (option: { value: string }, value: { value: string }) => option.value === value.value,
            label: 'Value',
            onChange: (_e: any, value: any) => handleValueChange(value),
            onInputChange: (_e: any, newInputValue: string) => handleInputValueChange(newInputValue),
            options: sendInteractionOptions ?? [],
            sx: { width: '33rem', ...sx?.autocomplete },
        };

        if (supportsMultiSelect) {
            const values = !!interactionAutocompleteValue
                ? Array.isArray(interactionAutocompleteValue)
                    ? interactionAutocompleteValue
                    : [interactionAutocompleteValue]
                : [];
            return (
                <Tooltip title={disabled && disabledTooltipContent ? disabledTooltipContent : ''}>
                    <span>
                        <Autocomplete
                            data-testid="select-multi-values-autocomplete"
                            multiple
                            placeholder={!!values?.length ? '' : 'Enter values'}
                            renderTags={(value, getTagProps) =>
                                value?.map((option, index) => (
                                    <Chip
                                        deleteIcon={<Close />}
                                        // label={option.labelWithSend}
                                        label={option.label}
                                        {...getTagProps({ index })}
                                        sx={greyChipStyles}
                                    />
                                ))
                            }
                            type="string"
                            value={values}
                            {...sharedProps}
                        />
                    </span>
                </Tooltip>
            );
        } else {
            return (
                <Tooltip title={disabled && disabledTooltipContent ? disabledTooltipContent : ''}>
                    <span>
                        <Autocomplete
                            data-testid="select-link-autocomplete"
                            data-test="select-link-autocomplete"
                            isOptionEqualToValue={(option: { value: string }, value: { value: string }) =>
                                option.value === value.value
                            }
                            placeholder="Enter a link"
                            type="string"
                            value={interactionAutocompleteValue ?? null}
                            {...sharedProps}
                        />
                    </span>
                </Tooltip>
            );
        }
    };

    return (
        <Stack direction="row" order={supportsMultiSelect ? '4' : '3'} alignItems="center">
            {!isValueAutocompleteVisible ? (
                <>
                    {selectedSends && (
                        <ToggleButtonGroup
                            data-testid="select-link-toggle-button-group"
                            data-test="select-link-toggle-button-group"
                            value="Value"
                            exclusive
                            aria-label="match type"
                            style={{ width: '42px', height: '42px' }}
                        >
                            <ToggleButton
                                data-testid="select-link-toggle-button"
                                data-test="select-link-toggle-button"
                                value="select-link"
                                style={{ width: '42px', height: '42px' }}
                                onClick={handleToggleButtonClick}
                            >
                                <Tooltip aria-label="select value" title="Select Value">
                                    <AdsClick />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </>
            ) : (
                renderAutocomplete()
            )}
        </Stack>
    );
};

export default ValueAutocomplete;
